import { authApi, uploadApi, setAuthTokens, clearAuthTokens } from "./index";

export const refreshToken = async (refreshTokenValue) => {
  try {
    const { tokenStorage } = await import("./index");
    const currentAccessToken = tokenStorage.getToken();

    const response = await authApi.post("/Account/generate-new-token", {
      accessToken: currentAccessToken,
      refreshToken: refreshTokenValue,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const signin = async (credentials) => {
  try {
    const response = await authApi.post("v2/AccountV/signin-with-email", credentials);

    // Automatically store tokens after successful signin
    if (response.data) {
      setAuthTokens(response.data);
    }
    return response;
  } catch (error) {
    throw error;
  }
};

export const signInWithPhoneNumber = async (countryCode, phoneNumber, otp) => {
  try {
    const response = await authApi.post(
      'v2/AccountV/signin-with-phone',
      { countryCode, phoneNumber, otp }
    );
    // Normalize shape and persist tokens + userId like email SignIn flow
    const top = response?.data || {};
    const payload = top?.data || top; // tokens are often under data

    if (payload) {
      // Persist tokens using centralized helper
      setAuthTokens(payload);

      // Store userId when available for consistency across app
      const userId =
        payload?.userDetails?.userId ||
        payload?.user?.userId ||
        payload?.data?.userDetails?.userId;
      if (userId) {
        localStorage.setItem('userId', userId);
      }
    }

    // Return the top-level API shape expected by callers
    return top;
  } catch (error) {
    throw error;
  }
};

export const forgetPassword = async (credentials) => {
  try {
    const response = await authApi.post(
      "/Account/forgot-password",
      credentials
    );
    return response.data;
  } catch (error) {
    throw new Error(error);
  }
};

export const validatePasswordToken = async (credentials) => {
  try {
    const response = await authApi.post(
      "/Account/validate-password-token",
      credentials
    );
    return response.data;
  } catch (error) {
    throw new Error(error);
  }
};

export const resetPassword = async (credentials) => {
  try {
    const response = await authApi.post("/Account/reset-password", credentials);
    return response.data;
  } catch (error) {
    throw new Error(error);
  }
};

export const sendEmailOtp = async (email) => {
  try {
    const response = await authApi.post(
      `v2/AccountV/send-email-otp?email=${encodeURIComponent(email)}`,
      {},
      {
        headers: {
          Accept: "*/*",
        },
      }
    );
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.message || "Error sending OTP");
  }
};

export const verifyEmailOtp = async (credentials) => {
  try {
    const response = await authApi.post(
      "v2/AccountV/verify-email-otp",
      credentials
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const verifyEmail = async (credentials) => {
  try {
    const response = await authApi.post(
      "v2/AccountV/check-email",
      credentials
    );
    return response.data;
  } catch (error) {
    //throw new Error(error);
    return error.response.data;
  }
};

export const verifyPhoneNumber = async (countryCode, phoneNumber) => {
  try {
    const response = await authApi.get(`v2/AccountV/Check-PhoneNumber`, {
      params: {
        countryCode,  
        phoneNumber,  
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const generateMobileOtp = async (phoneNumber) => {
  try {
    const response = await authApi.get(`v2/AccountV/Generate-PhoneNumber-Otp`, {
      params: {
        phonenumber: phoneNumber,
      },
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const signupWithPhoneNumber = async (countryCode, phoneNumber, otp, refreeAffiliateCode) => {
  try {
    const payload = {
      countryCode,
      phoneNumber,
      otp,
    };

    if (refreeAffiliateCode) {
      payload.refreeAffiliateCode = refreeAffiliateCode;
    }

    const response = await authApi.post(`v2/AccountV/Signup-With-PhoneNumber`, payload);

    // Automatically store tokens after successful signup
    if (response.data && response.data.success) {
      setAuthTokens(response.data);
      
      // Store user ID if available
      if (response.data.data?.userDetails?.userId) {
        localStorage.setItem('userId', response.data.data?.userDetails?.userId);
      }
    }

    return response.data;
  } catch (error) {
    throw error;
  }
};

export const signUp = async (credentials) => {
  try {
    const response = await authApi.post("v2/AccountV/Signup-With-Email", credentials);
    return response.data;
  } catch (error) {
    throw new Error(error);
  }
};

export const updateUser = async (credentials) => {
  try {
    const response = await authApi.put("/Account/update-user", credentials);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const logout = async () => {
  try {
    const response = await authApi.post("/Account/logout", {});

    // Clear stored tokens after successful logout
    clearAuthTokens();

    return response;
  } catch (error) {
    // Clear tokens even if logout fails
    clearAuthTokens();
    throw new Error(error?.response?.data?.message || "Logout failed");
  }
};

export const registerFace = async (credentials) => {
  try {
    const response = await authApi.post(
      "/Account/register-face-for-logged-in-user",
      credentials
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const signInWithFace = async (credentials) => {
  try {
    const response = await authApi.post(
      "/Account/signin-with-face",
      credentials
    );

    // Automatically store tokens after successful face signin
    if (response.data) {
      setAuthTokens(response.data);
    }

    return response;
  } catch (error) {
    throw error;
  }
};

export const userprofileImage = async (credentials) => {
  try {
    const response = await authApi.post(
      "/Upload/user-profile-image",
      credentials
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const uploadFile = async (credentials) => {
  try {
    const response = await uploadApi.post(
      "/Upload/upload-user-governament-id",
      credentials
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const deleteGovId = async (documentId) => {
  try {
    const response = await authApi.delete(
      `/Upload/delete-gov-id-by-id?documentId=${documentId}`
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const changePassword = async (credentials) => {
  try {
    const response = await authApi.post(
      "/Account/change-password",
      credentials
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const reaunthenticateWithFace = async (credentials) => {
  try {
    const response = await authApi.post(
      "/Account/reauthenticate-with-face",
      credentials
    );
    return response;
  } catch (error) {
    throw error;
  }
};

export const getAllQuestionnaireRoles = async () => {
  try {
    const response = await authApi.get(
      "QuestionariesRoles/get-all-questionaries-roles"
    );
    return response.data;
  } catch (error) {
    throw new Error(error);
  }
};

export const getUser = async (userId) => {
  try {
    const response = await authApi.get(`/Account/get-user/${userId}`);
    return response;
  } catch (error) {
    throw error;
  }
};

export const checkAffiliateCode = async (affiliateCode) => {
  try {
    const response = await authApi.get(
      `/Account/Check-AffilaiteCode?affiliateCode=${affiliateCode}`
    );
    return response.data;
  } catch (error) {
    throw error;
  }
};

// api/googleAuth.js
export const googleLoginSignup = async (idToken, refreeAffiliateCode = "") => {
  try {
    const response = await authApi.post(
      "v2/AccountV/Google-LoginSignup",
      {
        idToken,
        refreeAffiliateCode,
      }
    );
    return response.data;
  } catch (error) {
    console.error("Google login error:", error.response?.data || error.message);
    throw error;
  }
};

// api/facebookAuth.js
export const fbLoginSignup = async (accessToken, refreeAffiliateCode = "") => {
  try {
    const response = await authApi.post("v2/AccountV/Fb-LoginSignup", {
      accessToken,
      refreeAffiliateCode,
    });
    return response.data;
  } catch (error) {
    console.error("Facebook login error:", error.response?.data || error.message);
    throw error;
  }
};

// api redflagAuth.js
export const getFeatureFlags = async () => {
  try {
    const response = await authApi.get("v2/AccountV/Getflags");
    return response.data;
  } catch (error) {
    console.error("Error fetching feature flags:", error.response?.data || error.message);
    throw error;
  }
};

export const verifyReferralCode = async (referralCode) => {
  try {
    const response = await authApi.get(`/Account/Check-AffilaiteCode?affiliateCode=${referralCode}`);
    console.log("response", response);
    return response.data;
  } catch (error) {
    throw error;
  }
};

